import { useEffect, useId, useMemo, useState } from "react";
import { AlertCircle, CheckCircle2, Loader2, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/cn";
import { useDrawerA11y } from "@/hooks/useDrawerA11y";
import { useCompleteClient360Task } from "@/hooks/useClient360Tasks";
import { useCompleteTask, useEditTask, useTaskDetail } from "@/hooks/useTasks";
import { OutcomePicker, noteRequired } from "@/components/client360/OutcomePicker";
import { PaymentPromiseFields } from "@/components/client360/PaymentPromiseFields";
import {
  AttachmentUpload,
  type PendingAttachment,
} from "@/components/attachments/AttachmentUpload";
import type { Attachment } from "@/api/types";
import type {
  Client360TaskCompletePayload,
  PaymentPromiseInput,
  TaskOutcome,
  TaskSubtype,
} from "@/api/client360Tasks";

export interface CompleteTarget {
  id: string;
  title: string;
  subtype: TaskSubtype;
  /** client task → 3A endpoint + holding invalidation; null/absent → F3 endpoint. */
  holdingId?: string | null;
}

/**
 * Task Mgmt v1.2 — CANONICAL TaskCompleteModal (D-031 / AC-T25): the ONE
 * outcome-required completion form, harvested from 3A OutcomeCompleteDrawer and
 * reused from EVERY surface (Task list, detail drawer, Client360 tabs, Home).
 *  · «Завершити» disabled until an outcome is chosen (no one-click — §20)
 *  · outcomeNote required for other/blocked/not_needed
 *  · follow-up toggle → title + dueDate REQUIRED (VISUAL)
 *  · promised_payment → PaymentPromise P0-lite (дата обов'язкова; feeds Home/F6)
 * Endpoint routing: holdingId → POST /client360/tasks/:id/complete (3A cache),
 * else → POST /tasks/:id/complete (R1 gate accepts the same body).
 */
export function TaskCompleteModal({
  open,
  onClose,
  task,
  onCompleted,
}: {
  open: boolean;
  onClose: () => void;
  task: CompleteTarget | null;
  onCompleted?: () => void;
}) {
  const panelRef = useDrawerA11y(open, onClose);
  const headingId = useId();

  const holdingId = task?.holdingId ?? null;
  const mutation3A = useCompleteClient360Task(holdingId ?? "");
  const mutationF3 = useCompleteTask();
  const editMutation = useEditTask();
  const isPending =
    mutation3A.isPending || mutationF3.isPending || editMutation.isPending;

  // TASK-ATT v1.3 (W2/AC-COMPLETE) — fetch saved attachments while OPEN so the
  // user can add/view/remove before completion. The task is still open here, so
  // the bind (editTask) is allowed; removals = unlink (drop id from the list).
  const detailQuery = useTaskDetail(open && task ? task.id : null);
  const savedFromServer = useMemo<Attachment[]>(
    () => detailQuery.data?.attachments ?? [],
    [detailQuery.data],
  );

  const [outcome, setOutcome] = useState<TaskOutcome | "">("");
  const [note, setNote] = useState("");
  const [pp, setPp] = useState<PaymentPromiseInput>({ promisedPaymentDate: "", amount: null, createFollowUp: true });
  const [fuOn, setFuOn] = useState(false);
  const [fuTitle, setFuTitle] = useState("");
  const [fuDate, setFuDate] = useState("");
  const [err, setErr] = useState<string | null>(null);

  // Attachment state. `keptSavedIds` tracks which persisted attachments survive
  // (a remove drops the id → unlink on next bind). `pending` is the local queue.
  // `attTouched` flags whether the user changed anything → only then do we bind.
  const [keptSavedIds, setKeptSavedIds] = useState<string[] | null>(null);
  const [pending, setPending] = useState<PendingAttachment[]>([]);
  const [attTouched, setAttTouched] = useState(false);

  useEffect(() => {
    if (open) return;
    setOutcome(""); setNote("");
    setPp({ promisedPaymentDate: "", amount: null, createFollowUp: true });
    setFuOn(false); setFuTitle(""); setFuDate(""); setErr(null);
    setKeptSavedIds(null); setPending([]); setAttTouched(false);
  }, [open]);

  // Saved list the upload control renders = server attachments minus user removals.
  const savedShown = useMemo<Attachment[]>(
    () =>
      keptSavedIds == null
        ? savedFromServer
        : savedFromServer.filter((a) => keptSavedIds.includes(a.id)),
    [savedFromServer, keptSavedIds],
  );
  // FULL merged bound list to submit = kept saved ids + pending ids (🔴 partial
  // list UNLINKS omitted ones — always send the whole set).
  const mergedIds = useMemo(
    () => [...savedShown.map((a) => a.id), ...pending.map((p) => p.id)],
    [savedShown, pending],
  );

  if (!open || !task) return null;

  const subtype = task.subtype ?? "generic";
  const isPromise = outcome === "promised_payment";
  const needNote = noteRequired(outcome);
  const canSubmit =
    outcome !== "" &&
    (!needNote || note.trim().length > 0) &&
    (!isPromise || /^\d{4}-\d{2}-\d{2}$/.test(pp.promisedPaymentDate)) &&
    (!fuOn || (fuTitle.trim() !== "" && /^\d{4}-\d{2}-\d{2}$/.test(fuDate))) &&
    !isPending;

  const submit = async () => {
    if (outcome === "") { setErr("Оберіть результат — без нього задачу не закрити"); return; }
    if (needNote && !note.trim()) { setErr("Коментар обов'язковий для цього результату"); return; }
    if (isPromise && !/^\d{4}-\d{2}-\d{2}$/.test(pp.promisedPaymentDate)) { setErr("Вкажіть дату обіцянки оплати"); return; }
    if (fuOn && (!fuTitle.trim() || !/^\d{4}-\d{2}-\d{2}$/.test(fuDate))) {
      setErr("Follow-up не створюється без назви та дедлайну"); return;
    }
    const body: Client360TaskCompletePayload = {
      outcome,
      outcomeNote: note.trim() || undefined,
      paymentPromise: isPromise ? pp : undefined,
      nextActionDecision: fuOn ? "create_follow_up" : undefined,
      followUp: fuOn ? { dueDate: fuDate, title: fuTitle.trim() } : undefined,
    };
    try {
      // TASK-ATT v1.3 (W2) — the complete payload has NO attachments field, so
      // bind FIRST via editTask while the task is still open (allowed), sending
      // the FULL merged list. Only when the user actually touched attachments.
      if (attTouched) {
        await editMutation.mutateAsync({
          id: task.id,
          body: { attachmentsIds: mergedIds },
        });
      }
      if (holdingId) {
        await mutation3A.mutateAsync({ id: task.id, body });
      } else {
        await mutationF3.mutateAsync({ id: task.id, body });
      }
      onCompleted?.();
      onClose();
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Не вдалося завершити");
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose} aria-hidden>
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={headingId}
        tabIndex={-1}
        onClick={(e) => e.stopPropagation()}
        className="relative flex max-h-[88vh] w-full max-w-[480px] flex-col overflow-hidden rounded-2xl bg-background shadow-2xl outline-none"
      >
        <div className="flex items-center justify-between border-b px-5 py-4">
          <h3 id={headingId} className="flex items-center gap-2 text-base font-semibold">
            <CheckCircle2 className="h-4 w-4 text-primary" aria-hidden /> Завершити задачу
          </h3>
          <button type="button" onClick={onClose} aria-label="Закрити" className="rounded-md p-1 text-muted-foreground hover:text-foreground">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="flex-1 space-y-5 overflow-y-auto px-5 py-5">
          <div className="rounded-md border bg-muted/40 px-3 py-2 text-sm font-medium">{task.title}</div>

          <div>
            <label className="mb-1.5 block text-xs font-medium">
              Результат <span className="text-destructive">*</span>
            </label>
            <OutcomePicker subtype={subtype} value={outcome} onChange={setOutcome} />
            <p className="mt-1.5 text-[11.5px] text-muted-foreground">
              Результат обов'язковий (набір — за типом задачі). Без нього задача не закривається.
            </p>
          </div>

          <div>
            <label className="mb-1.5 block text-xs font-medium">
              Коментар {needNote && <span className="text-destructive">*</span>}
            </label>
            <Input
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Деталі результату…"
              className={cn(needNote && !note.trim() && err && "border-destructive")}
            />
          </div>

          {isPromise && <PaymentPromiseFields value={pp} onChange={setPp} />}

          {/* TASK-ATT v1.3 (W2 · AC-COMPLETE) — add/view/remove attachments
              BEFORE завершення. Task is still open → editable (no lock). On
              submit we bind via editTask, then complete. */}
          <div className="border-t pt-3">
            <AttachmentUpload
              parentType="Task"
              parentId={task.id}
              saved={savedShown}
              pending={pending}
              onChange={(next) => {
                setPending(next.pending);
                setAttTouched(true);
              }}
              onRemoveSaved={(id) => {
                setKeptSavedIds((prev) =>
                  (prev ?? savedFromServer.map((a) => a.id)).filter((x) => x !== id),
                );
                setAttTouched(true);
              }}
            />
          </div>

          {/* follow-up toggle (VISUAL: title + dueDate required) */}
          <div className="border-t pt-3">
            <button
              type="button"
              onClick={() => setFuOn((v) => !v)}
              aria-pressed={fuOn}
              className="flex w-full items-center justify-between text-left"
            >
              <span>
                <span className="block text-sm font-semibold">Потрібна наступна дія?</span>
                <span className="block text-[11px] text-muted-foreground">створити follow-up задачу</span>
              </span>
              <span
                className={cn(
                  "relative inline-flex h-[22px] w-[38px] shrink-0 rounded-full transition-colors",
                  fuOn ? "bg-primary" : "bg-muted-foreground/30",
                )}
                aria-hidden
              >
                <span
                  className={cn(
                    "absolute top-[2px] h-[18px] w-[18px] rounded-full bg-white transition-all",
                    fuOn ? "left-[18px]" : "left-[2px]",
                  )}
                />
              </span>
            </button>
            {fuOn && (
              <div className="mt-2 space-y-2.5 rounded-lg bg-primary/5 p-3">
                <div>
                  <label className="mb-1 block text-xs font-medium">
                    Follow-up: що зробити <span className="text-destructive">*</span>
                  </label>
                  <Input value={fuTitle} onChange={(e) => setFuTitle(e.target.value)} placeholder="Напр. Нагадати про оплату" />
                </div>
                <div>
                  <label className="mb-1 block text-xs font-medium">
                    Дедлайн follow-up <span className="text-destructive">*</span>
                  </label>
                  <Input type="date" value={fuDate} onChange={(e) => setFuDate(e.target.value)} />
                  <p className="mt-1 text-[11px] text-muted-foreground">follow-up не створюється без назви + дедлайну</p>
                </div>
              </div>
            )}
          </div>

          {err && (
            <p role="alert" className="flex items-center gap-1.5 text-xs text-destructive">
              <AlertCircle className="h-3.5 w-3.5" aria-hidden /> {err}
            </p>
          )}
        </div>

        <div className="flex gap-2 border-t bg-muted/30 px-5 py-4">
          <Button type="button" variant="outline" onClick={onClose} className="text-sm">
            Скасувати
          </Button>
          <Button type="button" onClick={submit} disabled={!canSubmit} className="h-10 flex-1 text-sm font-semibold">
            {isPending ? (<><Loader2 className="h-4 w-4 animate-spin" /> Завершення…</>) : "Завершити"}
          </Button>
        </div>
      </div>
    </div>
  );
}
